<script setup lang="ts">
import { provide, ref, computed } from "vue";
import { PRODUCT_PRICING_INJECTION_KEY } from "../../../constants/commerce";
import type { PriceItem, PricingState } from "../../../types";
import { createError, usePriceResource } from "#imports";

const props = withDefaults( defineProps<{
  tag?: string;
  productId: number | string;
  lazy?: boolean;
}>(), {
  tag: "div",
  priceId: undefined,
  lazy: false,
} );

const priceId = defineModel<number>();

const { listByProductId, listByProductKey } = usePriceResource();

const prices = await loadPriceData();

function loadPriceData () {
  if ( !props.productId ) {
    throw createError( {
      statusMessage: "ProductID not supplied",
    } );
  }

  if ( Number.isInteger( props.productId ) ) {
    return listByProductId( Number( props.productId ), props.lazy );
  } else {
    return listByProductKey( props.productId.toString() );
  }
}

const selectedPrice = computed( () => {
  return prices.data.value?.find( x => x.id === priceId.value ) ?? null;
} );

watch( prices.data, ( val ) => {
  if ( val?.length ) {
    const match = val.find( p => p.id === priceId.value );
    if ( match ) {
      priceId.value = match.id;
    } else {
      priceId.value = val[0].id;
    }
  } else {
    priceId.value = undefined;
  }
}, { immediate: true } );

function setPriceId ( id: number | undefined ) {
  priceId.value = id;
}

const quantity = ref( selectedPrice.value?.minimumOrderQuantity || 1 );

function setQuantity ( qty: number ) {
  quantity.value = qty;
}

const pricesData = computed( () => prices.data.value || [] as PriceItem[] );
const loading = computed( () => prices.status.value === "pending" );

const state = reactive<PricingState>( {
  productId: props.productId,
  priceId,
  prices: pricesData,
  selectedPrice: selectedPrice,
  loading,
  quantity,
  setPriceId,
  setQuantity,
} );

provide( PRODUCT_PRICING_INJECTION_KEY, state );
</script>

<template>
  <component :is="tag">
    <slot
      v-bind="state"
      :selected-price="selectedPrice" />
  </component>
</template>

<style scoped lang="scss">

</style>
